"use client";
import { usePostHog } from "@rallly/posthog/client";
import { Button } from "@rallly/ui/button";
import React from "react";
import { useSession } from "next-auth/react";

import { Trans } from "@/components/trans";
import { useBilling } from "@/features/billing/client";
import { env } from "@/env";

export const UpgradeButton = ({
  children,
  annual,
  className,
}: React.PropsWithChildren<{
  annual?: boolean;
  className?: string;
}>) => {
  const posthog = usePostHog();
  const { data: session } = useSession();
  const formRef = React.useRef<HTMLFormElement>(null);

  // Generate Wix upgrade URL using user ID as instance ID
  const wixUpgradeUrl = React.useMemo(() => {
    if (!session?.user?.id || !env.NEXT_PUBLIC_WIX_APP_ID) return null;
    
    // Use user ID as instance ID since all users are Wix users
    return `https://www.wix.com/apps/upgrade/${env.NEXT_PUBLIC_WIX_APP_ID}?appInstanceId=${session.user.id}`;
  }, [session?.user?.id]);

  // Always redirect to Wix upgrade URL since all users are Wix users
  if (wixUpgradeUrl) {
    return (
      <Button
        size="md"
        className={className}
        variant="primary"
        onClick={() => {
          window.open(wixUpgradeUrl, "_blank");
          posthog?.capture("click wix upgrade button");
        }}
      >
        {children || <Trans i18nKey="managePlan" defaults="Manage Plan" />}
      </Button>
    );
  }

  // Default Stripe upgrade flow
  return (
    <form ref={formRef} method="POST" action="/api/stripe/checkout">
      <input
        type="hidden"
        name="period"
        value={annual ? "yearly" : "monthly"}
      />
      <input
        type="hidden"
        name="return_path"
        value={window.location.pathname}
      />
      <Button
        size="md"
        className={className}
        variant="primary"
        onClick={(e) => {
          // 🐛 Since we have nested forms, we need to prevent the default
          // action of the button from being triggered so that we don't submit
          // the parent form.
          // TODO: Fix this by making sure we never have nested forms.
          e.preventDefault();
          formRef.current?.submit();
          posthog?.capture("click upgrade button");
        }}
      >
        {children || <Trans i18nKey="upgradeToPro" defaults="Upgrade to Pro" />}
      </Button>
    </form>
  );
};
